import { useMemo } from 'react';
import 'media/css/MeWidget.scss';
import { useAuth } from 'context/AuthContext';
import { useGetTodayStaffRank } from 'queries/analytics.query';
import __helpers from 'helpers/index';

interface StaffRankItem {
  user_id: number;
  display_name: string;
  user_avatar?: string | null;
  totalOrders: number;
  totalRevenue: number;
}

interface MotivationData {
  rank: number | null;
  ordersToday: number;
  topOrdersToday: number | null;
  nextCompetitorOrders: number | null;
}

type MotivationTone = 'winning' | 'chasing' | 'warning' | 'neutral';

type Motivation = {
  tone: MotivationTone;
  text: string;
};

const pickContent = (content: string[], seed: number): string => content[Math.abs(seed) % content.length];

const TOP_ONE_UNDER_PRESSURE_CONTENT = [
  'Top 1 mà có người núp ngay sau lưng thế này thì đừng mải ngắm cúp nhé. Chốt thêm đơn cho chắc ghế nào.',
  'Ngôi đầu đang hơi nóng rồi đấy. Người phía sau chỉ chực bạn chớp mắt là xin ngồi ké.',
  'Đang dẫn đầu, nhưng khoảng cách này mỏng như deadline cuối ngày. Thêm đơn để họ chỉ còn biết nhìn theo.',
  'Top 1 không có nghĩa là được nghỉ sớm đâu nha. Có người đang bật xi-nhan xin vượt rồi đó.',
  'Ghế số một đang có người dòm ngó. Làm thêm một đơn cho bảng xếp hạng biết ai là chủ nhà.',
];

const TOP_ONE_STEADY_CONTENT = [
  'Ngôi đầu hôm nay gọi tên bạn. Cứ giữ nhịp này, đối thủ chỉ còn nhiệm vụ cổ vũ từ xa.',
  'Top 1 rồi thì cứ bình tĩnh tỏa sáng. Nhưng đừng để cúp bụi, kiếm thêm đơn cho nó vui.',
  'Đầu bảng trông cũng hợp với bạn đấy. Giữ phong độ để cả team có chuyện mà bàn.',
  'Bạn đang dẫn đầu khá êm. Nhưng biết đâu một đơn nữa lại khiến khoảng cách đẹp hơn hẳn.',
  'Hôm nay bảng vàng đang ưu ái bạn. Cứ chốt đều tay, hào quang sẽ tự biết đường ở lại.',
];

const LOW_ACTIVITY_CONTENT = [
  'Ê ông sắp đoạt giải người nhàn nhất công ty rồi đấy. Mở hàng một đơn cho bảng thành tích đỡ cô đơn nào.',
  'Bảng thành tích hôm nay đang yên ắng quá. Cho nó một đơn đầu tiên để khỏi tưởng bạn đang nghỉ phép nhé.',
  'Đơn hôm nay ít đến mức máy tính cũng đang hỏi: “Có ai online không?” Chốt một đơn lấy vía nào.',
  'Nhịp này hơi thư giãn nha. Làm một đơn nhỏ thôi cũng đủ để cuộc đua có tiếng động rồi.',
  'Hôm nay bạn đang theo phong cách “lấy tĩnh chế động” à? Ra một đơn cho đội hình bớt hồi hộp nhé.',
];

const TIED_FOR_TOP_CONTENT = [
  'Đã ngang cơ top 1 rồi. Chốt thêm một đơn để khỏi phải dùng chung spotlight nào.',
  'Số đơn đã hòa top 1; giờ chỉ cần thêm một cú chốt là bảng xếp hạng đổi tông ngay.',
  'Đang đứng sát vạch đích cùng top 1. Ai nhanh tay hơn thì người đó được quyền ra vẻ.',
  'Top 1 đang có bạn đồng hành bất đắc dĩ rồi. Thêm một đơn để đi một mình cho oách.',
];

const CLOSE_TO_TOP_CONTENT = [
  'Cách top 1 có {gap} đơn thôi. Bật chế độ chốt đơn, kẻo họ tưởng bạn chỉ đến tham quan.',
  'Top 1 ngay trước mũi rồi: còn {gap} đơn. Lên tiếng một chút cho cuộc đua bớt buồn nào.',
  'Khoảng cách {gap} đơn là đủ để lật kèo trong một nhịp đẹp. Tới luôn đi chứ!',
  'Chỉ còn {gap} đơn nữa là chạm ngôi đầu. Đừng để cơ hội đứng đó rồi tự đi về nhé.',
  'Top 1 đang trong tầm với, cách {gap} đơn. Hôm nay mình làm người khiến bảng xếp hạng phải cập nhật liên tục nhé.',
];

const BEING_CHASED_CONTENT = [
  'Người phía sau đang bám sát lắm rồi. Chốt thêm đơn trước khi họ kịp bật nhạc ăn mừng nhé.',
  'Khoảng cách phía sau hơi mỏng nha. Làm thêm một đơn để họ phải đổi chiến thuật đi nào.',
  'Có đối thủ đang theo sát như thông báo chưa đọc. Xử lý thêm đơn để họ chỉ còn biết thả tim từ xa.',
  'Vị trí này chưa kịp ấm chỗ đâu. Nới khoảng cách ra một chút cho đỡ hồi hộp nhé.',
];

const KEEP_GOING_CONTENT = [
  'Nhịp hôm nay đang ổn. Thêm vài đơn nữa để bảng xếp hạng phải gọi đúng tên bạn.',
  'Cứ đều tay thế này là được. Một cú tăng tốc nhỏ biết đâu lại tạo bất ngờ lớn.',
  'Bạn đang vào guồng rồi đó. Đừng vội hạ ga, phía trên vẫn còn chỗ cho một màn leo hạng.',
  'Hôm nay có tiến triển đẹp. Chốt thêm vài đơn để cuối ngày xem lại còn thấy đã hơn nhé.',
];

const getInitials = (name: string): string => {
  const words = name.trim().split(/\s+/).filter(Boolean);

  if (!words.length) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();

  return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
};

const formatNumber = (value: number): string => new Intl.NumberFormat('vi-VN').format(value);

const formatMoney = (value: number, currency: string): string =>
  `${new Intl.NumberFormat('vi-VN', {
    maximumFractionDigits: 0,
  }).format(value)}${currency}`;

const buildMotivation = ({ rank, ordersToday, topOrdersToday, nextCompetitorOrders }: MotivationData): Motivation => {
  const contentSeed = (rank ?? 0) * 31 + ordersToday * 7 + (nextCompetitorOrders ?? 0);
  const getContent = (content: string[]): string => pickContent(content, contentSeed);
  const gapToTop = typeof topOrdersToday === 'number' ? Math.max(topOrdersToday - ordersToday, 0) : null;
  const gapFromBehind = typeof nextCompetitorOrders === 'number' ? Math.max(ordersToday - nextCompetitorOrders, 0) : null;

  if (rank === 1) {
    return {
      tone: gapFromBehind !== null && gapFromBehind <= 2 ? 'warning' : 'winning',
      text: getContent(gapFromBehind !== null && gapFromBehind <= 2 ? TOP_ONE_UNDER_PRESSURE_CONTENT : TOP_ONE_STEADY_CONTENT),
    };
  }

  if (ordersToday <= 1) {
    return {
      tone: 'neutral',
      text: getContent(LOW_ACTIVITY_CONTENT),
    };
  }

  if (gapToTop === 0) {
    return {
      tone: 'chasing',
      text: getContent(TIED_FOR_TOP_CONTENT),
    };
  }

  if (gapToTop !== null && gapToTop <= 3) {
    return {
      tone: 'chasing',
      text: getContent(CLOSE_TO_TOP_CONTENT).replace('{gap}', String(gapToTop)),
    };
  }

  if (gapFromBehind !== null && gapFromBehind <= 2) {
    return {
      tone: 'warning',
      text: getContent(BEING_CHASED_CONTENT),
    };
  }

  return {
    tone: 'neutral',
    text: getContent(KEEP_GOING_CONTENT),
  };
};

export default function MeInAction() {
  /** Dữ liệu người đang đăng nhập và bảng xếp hạng đều được lấy trực tiếp trong widget. */
  const { user } = useAuth();
  const { data, isLoading: loadingStaffRank } = useGetTodayStaffRank();

  const me = useMemo(() => {
    const normalizedStaff = (data ?? []).map<StaffRankItem>((staff) => ({
      ...staff,
      totalOrders: Number(staff.totalOrders) || 0,
      totalRevenue: Number(staff.totalRevenue) || 0,
    }));
    const currentStaff = normalizedStaff.find((staff) => String(staff.user_id) === String(user?.user_id));
    const rankedStaff = [...normalizedStaff].sort((first, second) => {
      if (second.totalRevenue !== first.totalRevenue) return second.totalRevenue - first.totalRevenue;
      if (second.totalOrders !== first.totalOrders) return second.totalOrders - first.totalOrders;
      return first.display_name.localeCompare(second.display_name, 'vi');
    });
    const rankIndex = rankedStaff.findIndex((staff) => String(staff.user_id) === String(user?.user_id));

    return {
      name: user?.display_name ?? currentStaff?.display_name ?? 'Nhân viên',
      role: user?.user_role ?? 'Nhân viên',
      user_nickname: user?.user_nickname ?? null,
      avatarUrl: user?.user_avatar ? __helpers.getMediaLink(user?.user_avatar) : null,
      ordersToday: currentStaff?.totalOrders ?? 0,
      profitToday: currentStaff?.totalRevenue ?? 0,
      rank: rankIndex >= 0 ? rankIndex + 1 : null,
      topOrdersToday: rankedStaff[0]?.totalOrders ?? null,
      nextCompetitorOrders: rankIndex >= 0 ? (rankedStaff[rankIndex + 1]?.totalOrders ?? null) : null,
    };
  }, [data, user]);

  const { avatarUrl, name, role, ordersToday, profitToday, rank, topOrdersToday, nextCompetitorOrders, user_nickname } = me;
  const currency = '₫';

  const motivation = useMemo(
    () =>
      buildMotivation({
        rank,
        ordersToday,
        topOrdersToday,
        nextCompetitorOrders,
      }),
    [rank, ordersToday, topOrdersToday, nextCompetitorOrders]
  );

  const topProgress = useMemo(() => {
    if (!topOrdersToday || topOrdersToday <= 0) return 0;
    return Math.min((ordersToday / topOrdersToday) * 100, 100);
  }, [ordersToday, topOrdersToday]);

  return (
    <section className="me-in-action" aria-label="Thành tích cá nhân hôm nay" aria-busy={loadingStaffRank}>
      <div className="me-in-action__glow me-in-action__glow--one" />
      <div className="me-in-action__glow me-in-action__glow--two" />

      <header className="me-in-action__header">
        <div className="me-in-action__identity">
          <div className="me-in-action__avatar-shell">
            {avatarUrl ? (
              <img className="me-in-action__avatar" src={avatarUrl} alt={name} />
            ) : (
              <span className="me-in-action__avatar me-in-action__avatar--fallback">{getInitials(name)}</span>
            )}
            <span className="me-in-action__online-dot" />
          </div>

          <div className="me-in-action__profile-copy">
            <span className="me-in-action__eyebrow">ME IN ACTION</span>
            <h3 className="me-in-action__name">
              {name} ({role})
            </h3>
            <p className="me-in-action__role">{user_nickname}</p>
          </div>
        </div>

        {rank ? (
          <div className="me-in-action__rank" title={`Xếp hạng ${rank}`}>
            <span className="me-in-action__rank-label">Hạng</span>
            <strong>#{rank}</strong>
          </div>
        ) : null}
      </header>

      <div className="me-in-action__stats">
        <article className="me-in-action__stat-card">
          <div className="me-in-action__stat-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M7 7h10l1 13H6L7 7Z" stroke="currentColor" strokeWidth="1.8" />
              <path d="M9.5 8V5.8a2.5 2.5 0 0 1 5 0V8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <div>
            <span className="me-in-action__stat-label">Số đơn hôm nay</span>
            <strong className="me-in-action__stat-value">{formatNumber(ordersToday)}</strong>
          </div>
        </article>

        <article className="me-in-action__stat-card">
          <div className="me-in-action__stat-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M4 17 9 12l3 3 7-8" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M15 7h4v4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div>
            <span className="me-in-action__stat-label">Doanh thu ngày</span>
            <strong className={`me-in-action__stat-value ${profitToday < 0 ? 'is-negative' : ''}`}>{formatMoney(profitToday, currency)}</strong>
          </div>
        </article>
      </div>

      {typeof topOrdersToday === 'number' && topOrdersToday > 0 ? (
        <div className="me-in-action__progress-block">
          <div className="me-in-action__progress-meta">
            <span>Tiến độ so với top 1</span>
            <strong>{Math.round(topProgress)}%</strong>
          </div>
          <div className="me-in-action__progress-track">
            <span style={{ width: `${topProgress}%` }} />
          </div>
        </div>
      ) : null}

      <div className={`me-in-action__motivation me-in-action__motivation--${motivation.tone}`}>
        <span className="me-in-action__motivation-icon" aria-hidden="true">
          {motivation.tone === 'warning' ? '!' : '↗'}
        </span>
        <p>{loadingStaffRank ? 'Đang cập nhật thành tích hôm nay...' : motivation.text}</p>
      </div>
    </section>
  );
}

